import { useMemo, useState } from 'react'

import { MOMENT_QUERY_FORMAT } from '@app/constants/Moment'

import type { Moment } from '@app/utils/moment'
import moment from '@app/utils/moment'
import { firstAvailableDay } from '@app/utils/timeHelper'

import { useAnnouncementsFilterOptions } from '@app/packages/announcementsFilter/useAnnouncementsFilterOptions'

import type { SavedAnnouncementsFilter } from './SavedAnnouncementsFilter'
import { useSavedAnnouncementsFilterManager } from './useSavedAnnouncementsFilterManager'

export const useSavedAnnouncementsFilterController = (initialState: SavedAnnouncementsFilter, now?: Moment) => {
  const minAvailableDate = useMemo(() => firstAvailableDay(now), [now])
  const [filter, setFilter] = useState(initialState)
  const { reset, effectiveFilter, set, update } = useSavedAnnouncementsFilterManager(filter, setFilter, minAvailableDate)

  const minDate: Moment | null = useMemo(() => toMoment(effectiveFilter.min_date) ?? null, [effectiveFilter.min_date])
  const maxDate: Moment | null = useMemo(() => toMoment(effectiveFilter.max_date) ?? null, [effectiveFilter.max_date])

  const {
    minHoursOptions,
    maxHoursOptions,
    startOptions,
    endOptions,
    childrenMinCapacityOptions,
    childrenMaxCapacityOptions,
    childrenMinAgeOptions,
    childrenMaxAgeOptions,
    regularOptions,
    statusOptions,
  } = useAnnouncementsFilterOptions(effectiveFilter)

  const minHoursOption = minHoursOptions.find(o => o.value === effectiveFilter.min_hours)
  const maxHoursOption = maxHoursOptions.find(o => o.value === effectiveFilter.max_hours)

  const startOption = startOptions.find(o => o.value === effectiveFilter.start)
  if (startOption?.value !== effectiveFilter.start) update('start', startOption?.value ?? null)
  const endOption = endOptions.find(o => o.value === effectiveFilter.end)
  if (endOption?.value !== effectiveFilter.end) update('end', endOption?.value ?? null)

  // childrenMinCapacity
  const childrenMinCapacityOption = useMemo(
    () => childrenMinCapacityOptions.find(o => o.value === effectiveFilter.min_kids),
    [childrenMinCapacityOptions, effectiveFilter.min_kids]
  )
  if (childrenMinCapacityOption?.value !== effectiveFilter.min_kids) update('min_kids', childrenMinCapacityOption?.value ?? null)

  // childrenMaxCapacity
  const childrenMaxCapacityOption = useMemo(
    () => childrenMaxCapacityOptions.find(o => o.value === effectiveFilter.max_kids),
    [effectiveFilter.max_kids, childrenMaxCapacityOptions]
  )
  if (childrenMaxCapacityOption?.value !== effectiveFilter.max_kids) update('max_kids', childrenMaxCapacityOption?.value ?? null)

  // childrenMinAge
  const childrenMinAgeOption = useMemo(
    () => childrenMinAgeOptions.find(o => o.value === effectiveFilter.youngest_age_months),
    [childrenMinAgeOptions, effectiveFilter.youngest_age_months]
  )
  if (childrenMinAgeOption?.value !== effectiveFilter.youngest_age_months) update('youngest_age_months', childrenMinAgeOption?.value ?? null)

  // childrenMaxAge
  const childrenMaxAgeOption = useMemo(
    () => childrenMaxAgeOptions.find(o => o.value === effectiveFilter.eldest_age_months),
    [effectiveFilter.eldest_age_months, childrenMaxAgeOptions]
  )
  if (childrenMaxAgeOption?.value !== effectiveFilter.eldest_age_months) update('eldest_age_months', childrenMaxAgeOption?.value ?? null)

  const regularOption = regularOptions.find(o => o.value === effectiveFilter.regular)

  const statusOption = useMemo(
    () => statusOptions.find(o => o.value === effectiveFilter.created_by) || { label: effectiveFilter.created_by, value: effectiveFilter.created_by },
    [statusOptions, effectiveFilter.created_by]
  )

  return useMemo(
    () => ({
      filter: effectiveFilter,
      rawFilter: filter,
      set,
      update,
      reset,
      minAvailableDate,
      minDate,
      maxDate,
      minHoursOption,
      minHoursOptions,
      maxHoursOption,
      maxHoursOptions,
      startOptions,
      startOption,
      endOptions,
      endOption,
      childrenMinCapacityOptions,
      childrenMinCapacityOption,
      childrenMaxCapacityOptions,
      childrenMaxCapacityOption,
      childrenMinAgeOptions,
      childrenMinAgeOption,
      childrenMaxAgeOptions,
      childrenMaxAgeOption,
      regularOptions,
      regularOption,
      statusOptions,
      statusOption,
    }),
    [
      effectiveFilter,
      filter,
      set,
      update,
      reset,
      minAvailableDate,
      minDate,
      maxDate,
      minHoursOption,
      minHoursOptions,
      maxHoursOption,
      maxHoursOptions,
      startOptions,
      startOption,
      endOptions,
      endOption,
      childrenMinCapacityOptions,
      childrenMinCapacityOption,
      childrenMaxCapacityOptions,
      childrenMaxCapacityOption,
      childrenMinAgeOptions,
      childrenMinAgeOption,
      childrenMaxAgeOptions,
      childrenMaxAgeOption,
      regularOptions,
      regularOption,
      statusOptions,
      statusOption,
    ]
  )
}

export type SavedAnnouncementFiltersController = ReturnType<typeof useSavedAnnouncementsFilterController>

const toMoment = (date: unknown) => {
  if (typeof date !== 'string' || !date) return undefined
  return moment(date, MOMENT_QUERY_FORMAT)
}
